import { Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useFetchData } from '../../../hooks/useFetchData';
import { useIndexRun } from '../../../hooks/useIndexRun';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { dataRequestListReIndex } from '../../../services/dealGPT';
import { StyledLoadingButton } from '../../../theme/CustomComponents';
import { ProjectRun, ProjectWithLastScanned } from '../../../Types/dealGPT';
import { ProjectRunStatus, RunType } from '../../../Types/enums';
import { getLatestProjectRunPathUrl } from '../../../util/routesUrls';

interface DataRequestListReIndexButtonProps {
  project: ProjectWithLastScanned;
  refetchProject: () => Promise<void>;
  refetchDataRequestList: () => Promise<void>;
}

export const DataRequestListReIndexButton = ({
  project,
  refetchProject,
  refetchDataRequestList,
}: DataRequestListReIndexButtonProps): JSX.Element => {
  const {
    data: drlRun,
    refetch: refetchDrlRun,
    startPolling: startPollingDrlRun,
    stopPolling: stopPollingDrlRun,
  } = useFetchData<ProjectRun>({
    urlPath: getLatestProjectRunPathUrl(project.id, RunType.DATA_REQUEST_LIST),
  });
  const { currentFolder } = useSelector(selectFolders);
  const { isIndexing, lastIndexDate } = useIndexRun(currentFolder?.indexIds?.[0]);
  const hasRefetchedOnComplete = useRef(false);

  const { isIndexingDrl, isRunningDiscovery, isCompletedSuccessfully } = useMemo(() => {
    // Check if we are doing DRL specific indexing
    const isIndexingDrl = drlRun?.details === 'Indexing';
    // Check if we are running discovery
    const isRunningDiscovery =
      drlRun?.details === ProjectRunStatus.DISCOVERY ||
      project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS;
    // Check if DRL run completed successfully
    const isCompletedSuccessfully = drlRun?.details === ProjectRunStatus.COMPLETED_SUCCESSFULLY;
    return { isIndexingDrl, isRunningDiscovery, isCompletedSuccessfully };
  }, [drlRun?.details, project.projectRunStatus]);

  const handleClick = useCallback(async () => {
    await dataRequestListReIndex(project.id, project.indexToUse);
    await refetchDrlRun();
    await refetchProject();
  }, [project.id, project.indexToUse, refetchDrlRun, refetchProject]);

  // Poll DRL run while indexing or running discovery
  useEffect(() => {
    if (isIndexingDrl || isRunningDiscovery) {
      startPollingDrlRun(20000);
    }

    return () => stopPollingDrlRun();
  }, [isIndexingDrl, isRunningDiscovery, startPollingDrlRun, stopPollingDrlRun]);

  // When DRL run completes, refetch data request list
  useEffect(() => {
    if (isCompletedSuccessfully && !hasRefetchedOnComplete.current) {
      hasRefetchedOnComplete.current = true;
      refetchDataRequestList();
    }
  }, [isCompletedSuccessfully, refetchDataRequestList]);

  return (
    <Tooltip
      title={`Last indexed: ${
        lastIndexDate ? new Date(lastIndexDate).toLocaleString() : 'Not indexed'
      }.`}
    >
      <StyledLoadingButton
        variant="contained"
        loadingPosition="end"
        onClick={handleClick}
        loading={isIndexing || isIndexingDrl || isRunningDiscovery}
        sx={{ width: '150px' }}
      >
        {isIndexing || isIndexingDrl ? 'Indexing' : isRunningDiscovery ? 'Discovery' : 'Re-Index'}
      </StyledLoadingButton>
    </Tooltip>
  );
};
